// FILE: src/components/testimonials/TestimonialCard.js
//
// One testimonial, presentational only. Shared by the public carousel and
// the admin manager's live preview so what admins see is exactly what ships.
//
// Safety: all content renders as React text nodes (escaped — no
// dangerouslySetInnerHTML anywhere), external links carry
// rel="noopener noreferrer nofollow". Cards without a photo (or whose photo
// 404s) simply show no avatar — text only, per Bryan's direction.

import React from 'react';
import { getExamConfig } from '../../config/exams';
import styles from './TestimonialsCarousel.module.css';

/** '2026-05-14' → 'May 2026' (parsed as plain y/m to avoid TZ drift). */
export function formatCertDate(isoDate) {
  if (!isoDate) return null;
  const [y, m] = String(isoDate).split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return null;
  const months = ['January','February','March','April','May','June','July',
    'August','September','October','November','December'];
  return `${months[m - 1]} ${y}`;
}

/** Cert display name from the canonical config; falls back to the raw code. */
export function certLabelFor(examId) {
  const cfg = getExamConfig(examId);
  return (cfg && cfg.displayName) || examId || '';
}


export default function TestimonialCard({ testimonial, onLinkClick }) {
  const t = testimonial || {};
  const [imgFailed, setImgFailed] = React.useState(false);
  const showImg = Boolean(t.profile_image_url) && !imgFailed;
  const certLabel = certLabelFor(t.exam_id);
  const certDate = formatCertDate(t.certification_date);
  const subtitle = [t.professional_title, t.organization].filter(Boolean).join(', ');

  return (
    <figure className={styles.card} data-testid="testimonial-card">
      <blockquote className={styles.quote}>{t.testimonial_text}</blockquote>
      <figcaption className={styles.attribution}>
        {showImg && (
          <img
            className={styles.avatar}
            src={t.profile_image_url}
            alt=""
            width="48"
            height="48"
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
          />
        )}
        <span className={styles.who}>
          <span className={styles.name}>{t.display_name}</span>
          {subtitle && <span className={styles.role}>{subtitle}</span>}
          <span className={styles.certLine}>
            {certLabel && (
              <span className={styles.certBadge} data-testid="testimonial-cert">{certLabel}</span>
            )}
            {certDate && <span className={styles.certDate}>{certDate}</span>}
            {t.attribution_label && (
              <span className={styles.attributionLabel}>{t.attribution_label}</span>
            )}
          </span>
          {(t.linkedin_url || t.verification_url) && (
            <span className={styles.links}>
              {t.linkedin_url && (
                <a
                  href={t.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className={styles.extLink}
                  onClick={() => onLinkClick && onLinkClick('linkedin')}
                >
                  LinkedIn
                </a>
              )}
              {t.verification_url && (
                <a
                  href={t.verification_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className={styles.extLink}
                  onClick={() => onLinkClick && onLinkClick('verification')}
                >
                  Verify certification
                </a>
              )}
            </span>
          )}
        </span>
      </figcaption>
    </figure>
  );
}
